import type { Notification } from '@tandemone/types'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowUpRight, Check, X } from 'lucide-react'
import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router'

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { reportError } from '@/domains/observability'
import { initials } from '@/lib/initials'
import { cn } from '@/lib/utils'

import { dismissNotification, markNotificationRead } from './api'
import { useNotificationRecipient } from './recipient'
import { relativeTime } from './relative-time'
import { getNotificationRenderer } from './renderers'

/** page-flavoured notification row. distinct from the palette row in
 * notifications-section.tsx because the palette one lives inside CommandItem
 * and has cmdk-driven click semantics. this one is a plain button-row used
 * by /notifications.
 */
export function NotificationRow({
  notification
}: {
  notification: Notification
}) {
  const renderer = getNotificationRenderer(notification)
  const TypeIcon = renderer.icon
  const isUnread = notification.readAt === null
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const recipient = useNotificationRecipient()
  const link = renderer.link(notification.payload, recipient)

  const markRead = useMutation({
    mutationFn: () => markNotificationRead(notification.id),
    onError: reportError
  })

  const dismiss = useMutation({
    mutationFn: () => dismissNotification(notification.id),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ['notifications', 'feed'] })
    },
    onError: reportError,
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: ['notifications', 'feed']
      })
    }
  })

  const title = renderer.title(notification.payload)
  const description = renderer.description(notification.payload)
  const hasSender = notification.senderUserId !== null

  function handleSelect() {
    if (isUnread) markRead.mutate()
    if (link) void navigate(link)
  }

  /** auto-mark-read on viewport: an unread row that stays ≥50% in view for
   * ~1.5s fires markRead once. the firedRef latch protects against rapid
   * re-intersect (scroll jitter) and against a viewport exit while the
   * mutation is in flight re-firing on the next entry.
   *
   * depending on markRead.mutate (stable in tanstack query v5) instead of
   * the full mutation object keeps a sibling-query invalidation mid-dwell
   * from tearing the observer down and restarting the timer.
   */
  const rowRef = useRef<HTMLDivElement | null>(null)
  const firedRef = useRef(false)
  const markReadMutate = markRead.mutate
  useEffect(() => {
    if (!isUnread || firedRef.current) return
    if (typeof IntersectionObserver === 'undefined') return
    const node = rowRef.current
    if (!node) return
    let timer: ReturnType<typeof setTimeout> | null = null
    const observer = new IntersectionObserver(
      entries => {
        const entry = entries[0]
        if (!entry) return
        if (entry.isIntersecting) {
          if (timer) return
          timer = setTimeout(() => {
            if (firedRef.current) return
            firedRef.current = true
            markReadMutate()
            observer.disconnect()
          }, 1500)
        } else if (timer) {
          clearTimeout(timer)
          timer = null
        }
      },
      { threshold: 0.5 }
    )
    observer.observe(node)
    return () => {
      observer.disconnect()
      if (timer) clearTimeout(timer)
    }
  }, [isUnread, markReadMutate])

  return (
    <div
      ref={rowRef}
      role='button'
      tabIndex={0}
      onClick={handleSelect}
      onKeyDown={event => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          handleSelect()
        }
      }}
      className={cn(
        'group relative flex cursor-pointer items-start gap-3 border-b border-border px-5 py-4 transition-colors last:border-b-0 hover:bg-cream/60',
        isUnread && 'bg-accent-light/40 hover:bg-accent-light/60'
      )}
    >
      {isUnread && (
        <span
          aria-hidden='true'
          className='absolute left-0 top-0 h-full w-[3px] bg-accent'
        />
      )}

      {/* avatar + type badge */}
      <div className='relative shrink-0'>
        {hasSender ? (
          <Avatar size='lg' className='size-9'>
            {notification.senderImage && (
              <AvatarImage
                src={notification.senderImage}
                alt={notification.senderName ?? ''}
              />
            )}
            <AvatarFallback className='bg-accent text-[11px] font-semibold text-white'>
              {initials(notification.senderName)}
            </AvatarFallback>
          </Avatar>
        ) : (
          <div className='flex size-9 items-center justify-center rounded-full bg-accent-light text-accent dark:text-accent-mid'>
            <TypeIcon className='size-[17px]' />
          </div>
        )}
        {/* type badge: appears for sender-based avatars so the kind is still
         * legible at a glance. omitted on system rows (already an icon). */}
        {hasSender && (
          <span
            aria-hidden='true'
            className='absolute -bottom-0.5 -right-0.5 inline-flex size-[18px] items-center justify-center rounded-full bg-cream text-accent dark:text-accent-mid ring-2 ring-page'
          >
            <TypeIcon className='size-[10px]' />
          </span>
        )}
      </div>

      {/* content */}
      <div className='flex min-w-0 flex-1 flex-col gap-0.5'>
        <div className='flex items-baseline gap-3'>
          {/* the badge sits outside the clamped span so it cannot be eaten */}
          <p
            className={cn(
              'min-w-0 flex-1 text-[13.5px] leading-tight',
              isUnread ? 'font-medium text-ink' : 'font-normal text-ink-soft'
            )}
          >
            <span className='line-clamp-2 sm:truncate'>{title}</span>
            {notification.critical && (
              <span className='mt-1 inline-flex items-center rounded-full bg-gold-light px-1.5 py-0.5 text-[9px] font-medium tracking-wide text-gold uppercase sm:mt-0 sm:ml-2'>
                Important
              </span>
            )}
          </p>
          <span className='inline-flex shrink-0 items-center gap-1 text-[11px] text-ink-muted'>
            {relativeTime(notification.createdAt)}
            {link && (
              <ArrowUpRight
                aria-hidden='true'
                className='size-3 text-ink-muted'
              />
            )}
          </span>
        </div>
        <p
          className={cn(
            'line-clamp-2 text-[12.5px] leading-snug sm:truncate',
            isUnread ? 'text-ink-soft' : 'text-ink-muted'
          )}
        >
          {description}
        </p>
      </div>

      {/* in flow below sm: absolute + opacity-0 would take invisible taps
          over the meta on a device that never hovers them back. */}
      <div className='flex shrink-0 items-start gap-1 self-start transition-opacity group-focus-within:opacity-100 sm:absolute sm:top-3 sm:right-4 sm:opacity-0 sm:group-hover:opacity-100'>
        {isUnread && (
          <button
            type='button'
            aria-label='Mark as read'
            onClick={event => {
              event.stopPropagation()
              markRead.mutate()
            }}
            disabled={markRead.isPending}
            className='inline-flex size-9 items-center justify-center gap-1 rounded-md border border-border bg-page text-[10px] font-medium text-ink-soft transition-colors hover:bg-cream hover:text-ink disabled:opacity-50 sm:size-auto sm:px-2 sm:py-1'
          >
            <Check className='size-3' />
            <span className='hidden sm:inline'>Read</span>
          </button>
        )}
        <button
          type='button'
          aria-label='Dismiss'
          onClick={event => {
            event.stopPropagation()
            dismiss.mutate()
          }}
          disabled={dismiss.isPending}
          className='inline-flex size-9 items-center justify-center rounded-md border border-border bg-page text-ink-muted transition-colors hover:bg-cream hover:text-ink disabled:opacity-50 sm:size-auto sm:p-1'
        >
          <X className='size-3.5' />
        </button>
      </div>
    </div>
  )
}
